import { Link } from 'react-router-dom-v5-compat';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  CUSTOM_BUTTON_COLOR,
  CUSTOM_BUTTON_VARIANT,
  CustomButton,
} from 'src/components/CustomButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { UsageProgress } from 'src/components/UsageProgress';
import { customerInTrial, customerTrialEnded } from 'src/data/customer';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { ROUTES } from 'src/routes';
import { useCurrentCustomer, useCustomFeatureLimitsData } from 'src/store';

import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

export const TrialProgress = ({ className }) => {
  const styles = useStyles();

  const { handleUpgradeClick } = useHandlers();

  const currentCustomer = useCurrentCustomer();
  const predictionsLimitsData = useCustomFeatureLimitsData(FEATURE_LIMITS.performancePredictions);

  const isFeatureLimited = predictionsLimitsData?.currentCount >= predictionsLimitsData?.limit;
  const isFeatureHalfUsed = predictionsLimitsData?.currentCount >= predictionsLimitsData?.limit / 2;

  if (!customerInTrial(currentCustomer) && !customerTrialEnded(currentCustomer)) {
    return null;
  }

  return (
    <div className={cx(styles.root, className)}>
      <CustomTypography
        className={styles.title}
        variant={TYPOGRAPHY_VARIANT.semiBold16}
      >
        {isFeatureLimited ? "You've reached the limit!" : 'Free trial'}
      </CustomTypography>
      <div className={styles.usageTitles}>
        <CustomTypography
          className={styles.usageTitle}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          Performance predictions
        </CustomTypography>
        <CustomTypography
          className={cx(styles.usageTitle, { [styles.usageTitleLimited]: isFeatureLimited })}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          <span>{predictionsLimitsData.currentCount}</span>/
          <span>{predictionsLimitsData.limit}</span>
        </CustomTypography>
      </div>
      <UsageProgress
        classes={{
          bar: isFeatureLimited
            ? styles.limitBar
            : isFeatureHalfUsed
              ? styles.halfLimitBar
              : styles.bar,
          linearProgress: styles.linearProgress,
        }}
        value={predictionsLimitsData.currentCount}
        limit={predictionsLimitsData.limit}
      />
      <CustomTypography
        className={styles.description}
        variant={TYPOGRAPHY_VARIANT.regular12}
      >
        {isFeatureLimited
          ? 'Upgrade to a paid plan to get more predictions.'
          : 'Performance predictions show how effective your copy will be before it’s published. Upgrade to get more.'}
      </CustomTypography>

      <CustomButton
        className={styles.button}
        onClick={() => handleUpgradeClick(predictionsLimitsData.currentCount)}
        component={Link}
        variant={CUSTOM_BUTTON_VARIANT.text}
        color={CUSTOM_BUTTON_COLOR.primary}
        to={ROUTES.UPGRADE}
      >
        Upgrade
      </CustomButton>
    </div>
  );
};

TrialProgress.propTypes = {
  className: PropTypes.string,
};
TrialProgress.defaultProps = {
  className: '',
};
